import { clsx, type ClassValue } from 'clsx';
import { format, parseISO } from 'date-fns';
import { twMerge } from 'tailwind-merge';

import type { RrgQuadrant } from '../types';

/**
 * Safely merges Tailwind classes, preventing conflicts when injecting
 * dynamic conditional colors (mirrors the treasury-curve sibling util).
 */
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/** Safely formats the ISO date string from the backend. */
export function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '--';
    try {
        return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
        return dateString;
    }
}

/** Formats a fractional return (0.0512) as a signed percent (+5.12%). */
export function formatPct(value: number | null | undefined, digits = 2): string {
    if (value == null || Number.isNaN(value)) return '--';
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${(value * 100).toFixed(digits)}%`;
}

/** Formats a plain ratio / multiple (e.g. a P/E of 24.3). */
export function formatNum(value: number | null | undefined, digits = 2): string {
    if (value == null || Number.isNaN(value)) return '--';
    return value.toFixed(digits);
}

/** Formats a 0..1 confidence as a whole percent (29%). */
export function formatConfidence(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '--';
    return `${Math.round(value * 100)}%`;
}

// --- RRG QUADRANT THEMING ----------------------------------------------
// A single source of truth for the 4-quadrant colour language shared by the
// scatter plot (hex, for the SVG) and the rankings table (Tailwind classes).

export interface QuadrantTheme {
    hex: string;
    badge: string;
    label: RrgQuadrant;
}

export const QUADRANT_THEME: Record<RrgQuadrant, QuadrantTheme> = {
    Leading: {
        hex: '#10b981', // emerald-500
        badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
        label: 'Leading',
    },
    Weakening: {
        hex: '#f59e0b', // amber-500
        badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
        label: 'Weakening',
    },
    Lagging: {
        hex: '#f43f5e', // rose-500
        badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800',
        label: 'Lagging',
    },
    Improving: {
        hex: '#0ea5e9', // sky-500
        badge: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800',
        label: 'Improving',
    },
    Unknown: {
        hex: '#94a3b8', // slate-400
        badge: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
        label: 'Unknown',
    },
};

export function quadrantTheme(q: string | null | undefined): QuadrantTheme {
    return QUADRANT_THEME[(q as RrgQuadrant) ?? 'Unknown'] ?? QUADRANT_THEME.Unknown;
}

// --- SECTOR ETF NAMES --------------------------------------------------
// Canonical GICS SPDR sector-ETF display names, mirroring the backend
// ``GICS_SECTORS`` taxonomy (taxonomy.py). Endpoints that only carry the raw
// ticker (correlation matrix, valuations) use this to enrich the UI so users
// see the human-readable sector alongside the ticker.
export const SECTOR_NAMES: Record<string, string> = {
    XLK: 'Technology',
    XLF: 'Financials',
    XLV: 'Healthcare',
    XLE: 'Energy',
    XLY: 'Consumer Discretionary',
    XLP: 'Consumer Staples',
    XLI: 'Industrials',
    XLB: 'Basic Materials',
    XLU: 'Utilities',
    XLRE: 'Real Estate',
    XLC: 'Communication Services',
    SPY: 'S&P 500',
};

/** Resolves a sector ETF ticker to its human-readable name (empty if unknown). */
export function sectorName(ticker: string | null | undefined): string {
    if (!ticker) return '';
    return SECTOR_NAMES[ticker.toUpperCase()] ?? '';
}

/**
 * Diverging correlation -> CSS colour. -1 (rose) .. 0 (slate) .. +1 (indigo).
 * Returns an rgba string so the heatmap cell can set it inline; opacity scales
 * with |rho| so weak correlations fade toward the neutral background.
 */
export function correlationColor(rho: number): string {
    const clamped = Math.max(-1, Math.min(1, rho));
    const alpha = 0.15 + Math.abs(clamped) * 0.75;
    if (clamped >= 0) {
        // Indigo-600 (99,102,241 is indigo-500; use 79,70,229 for depth).
        return `rgba(79, 70, 229, ${alpha.toFixed(3)})`;
    }
    // Rose-500.
    return `rgba(244, 63, 94, ${alpha.toFixed(3)})`;
}
